import { useState, useRef, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Divider,
  makeStyles,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Description, CloudUpload, ArrowRight } from "@material-ui/icons";
import { useAuth } from "../context/ApiKeyContext";
import { v4 as uuidv4 } from "uuid";
import { BoxRenderResult } from "./RenderHTML";
import ExtractionModeChip from "./ExtractionModeChip";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";
import {
  EXTRACTION_MODES,
  EXTRACTION_MODE_UI_DEFAULT,
  normalizeExtractionMode,
} from "../constants/extractionModes";

const API_BASE_URL = getApiBaseUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  helperText: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1.75),
    color: "var(--ikaro-text-secondary)",
  },
  processingBox: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(3),
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    marginLeft: theme.spacing(1),
  },
  fileName: {
    marginLeft: theme.spacing(1),
    flexGrow: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  resultHeader: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    textAlign: "left",
    borderBottom: "2px solid var(--ikaro-gray-light)",
    paddingBottom: theme.spacing(1),
  },
  resultSubHeader: {
    fontWeight: "bold",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  resultItem: {
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  resultList: {
    textAlign: "left",
    paddingTop: theme.spacing(1),
  },
  listBullet: {
    display: "inline-block",
    // marginRight: theme.spacing(0.5),
    fontSize: "12px",
    color: theme.palette.primary.main,
  },
}));

const DocumentProcessor = ({
  templateData,
  initialJobName = null,
  initialPdfFile = null,
  initialExtractionMode = null,
}) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  // Usar o token do contexto global
  const { token, cell } = useAuth();
  const { t } = useTranslation("editor");
  const [externalId, setExternalId] = useState("");
  const [autoGenExternalId, setAutoGenExternalId] = useState(true);
  const [jobName, setJobName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [rawResult, setRawResult] = useState(null);
  /* Modo em que o job REALMENTE rodou, lido da resposta do polling. Pode
     divergir do seletor: se o external_id já existia, o endpoint devolve o
     job anterior e descarta o modo enviado. */
  const [resultExtractionMode, setResultExtractionMode] = useState(null);
  const [cancelProcessing, setCancelProcessing] = useState(false);
  /* A API assume Standard quando o campo não vem; a tela vem com Next-Set26
     pré-selecionado. São defaults diferentes de propósito — por isso o campo
     é enviado sempre (ver processDocument), nunca só quando o usuário mexe. */
  const [extractionMode, setExtractionMode] = useState(
    EXTRACTION_MODE_UI_DEFAULT,
  );

  useEffect(() => {
    if (initialJobName != null && String(initialJobName).trim() !== "") {
      setJobName(String(initialJobName).trim());
    }
  }, [initialJobName]);

  useEffect(() => {
    if (initialPdfFile instanceof File) {
      setSelectedFile(initialPdfFile);
    }
  }, [initialPdfFile]);

  /* Replicação: o job de origem manda o modo em que rodou, para a réplica sair
     igual ao original em vez de cair no default da tela. */
  useEffect(() => {
    if (initialExtractionMode != null && String(initialExtractionMode) !== "") {
      const mode = normalizeExtractionMode(initialExtractionMode);
      if (EXTRACTION_MODES.includes(mode)) {
        setExtractionMode(mode);
      }
    }
  }, [initialExtractionMode]);

  // Função para diagnóstico do template
  useEffect(() => {
    if (templateData) {
      console.log("➡️ Template atualizado no DocumentProcessor:", templateData);
      console.log("➡️ Tipo:", typeof templateData);
      console.log("➡️ Número de chaves:", Object.keys(templateData).length);
      console.log("➡️ Chaves:", Object.keys(templateData));
    } else {
      console.log("➡️ Template é null/undefined no DocumentProcessor");
    }
  }, [templateData]);

  // Função para sanitizar chaves do template (remove espaços)
  const sanitizeTemplateKeys = (obj) => {
    if (typeof obj !== "object" || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => sanitizeTemplateKeys(item));
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Remove espaços das chaves e substitui por underscore
      const sanitizedKey = key.replace(/\s+/g, "_");

      // Verifica se há espaços na chave original
      if (key !== sanitizedKey) {
        console.warn(
          `Chave "${key}" foi sanitizada para "${sanitizedKey}" (espaços não são permitidos)`,
        );
      }

      // Recursivamente sanitiza objetos aninhados
      sanitized[sanitizedKey] = sanitizeTemplateKeys(value);
    }
    return sanitized;
  };

  // Função para formatar o template para o formato correto da API
  const formatTemplateForAPI = (template) => {
    // Se não houver template, retorna um objeto vazio
    if (!template) return {};

    // Log do template original para diagnóstico
    console.log("Template original recebido (objeto):", template);
    console.log(
      "Template original recebido (string):",
      JSON.stringify(template),
    );
    console.log("Template tem chaves?", Object.keys(template).length);

    try {
      // Faz uma cópia profunda do objeto para não modificar o original
      const formattedTemplate = JSON.parse(JSON.stringify(template));

      // Verificamos se o template parece ter o formato correto (pelo menos uma chave)
      if (Object.keys(formattedTemplate).length === 0) {
        console.error(
          "O template parece estar vazio. Verifique se foi criado corretamente.",
        );
        throw new Error(t("alerts.invalidEmptyTemplate"));
      }

      // Sanitiza as chaves do template (remove espaços)
      const sanitizedTemplate = sanitizeTemplateKeys(formattedTemplate);

      // Verificação final do formato
      console.log("Template formatado para API:", sanitizedTemplate);

      return sanitizedTemplate;
    } catch (error) {
      console.error("Erro ao formatar template:", error);
      throw new Error("Erro ao processar o template: " + error.message);
    }
  };

  const handleFileSelect = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const validateInputs = () => {
    if (!token) {
      setError(t("common:authTokenNotFound"));
      return false;
    }
    if (!externalId.trim() && !autoGenExternalId) {
      setError(t("alerts.externalIdRequired"));
      return false;
    }
    if (!jobName.trim()) {
      setError(t("alerts.taskNameRequired"));
      return false;
    }
    if (!selectedFile) {
      setError(t("alerts.selectPdf"));
      return false;
    }
    // Verificar se o template é válido (não está vazio)
    if (!templateData) {
      console.error("Template is null/undefined");
      setError(t("alerts.needValidTemplate"));
      return false;
    }

    console.log("Validating template:", templateData);
    console.log("Number of keys:", Object.keys(templateData).length);

    if (Object.keys(templateData).length === 0) {
      console.error("Template is empty (no keys)");
      setError(t("alerts.needValidTemplate"));
      return false;
    }
    return true;
  };

  const processDocument = async () => {
    // Validar entradas antes de processar
    if (!validateInputs()) return;

    // Preparar o external ID (gerado ou inserido pelo usuário)
    let idToUse = externalId;

    // Gerar um UUID se a opção estiver habilitada
    if (autoGenExternalId) {
      idToUse = uuidv4();
      setExternalId(idToUse); // Atualiza o campo visual, mas não esperamos que seja processado a tempo
    }

    setError(null);
    setIsProcessing(true);
    setProcessingStatus(t("startingProcessing"));
    setProcessingProgress(10);
    setResult(null);
    setResultExtractionMode(null);

    try {
      // Preparar os dados para a API
      const formData = new FormData();
      formData.append("arquivo_pdf", selectedFile);
      formData.append("job_name", jobName);

      // Preparar o template JSON
      let jsonData;

      try {
        // Tenta formatar o template - agora pode lançar uma exceção se inválido
        jsonData = formatTemplateForAPI(templateData);
      } catch (templateError) {
        setError(templateError.message);
        setIsProcessing(false);
        return;
      }

      // Verificação final - não enviar se o JSON estiver vazio
      if (
        JSON.stringify(jsonData) === "{}" ||
        JSON.stringify(jsonData) === "[]"
      ) {
        setError(t("alerts.emptyTemplate"));
        setIsProcessing(false);
        return;
      }

      // Adicionamos cliente como campos separados no FormData (não dentro do JSON)
      formData.append("cliente", cell?.value);

      // Convertemos o template para uma string JSON e adicionamos ao FormData
      formData.append("data", JSON.stringify(jsonData));

      /* Campo multipart, NUNCA query string: o endpoint responde 400 de
         propósito se extraction_mode vier na query (ele seria ignorado em
         silêncio e o job rodaria em Standard). Enviado incondicionalmente
         porque o default da tela difere do default da API. */
      formData.append("extraction_mode", extractionMode);

      // Configurar URL com parâmetros - usando o ID preparado, não o estado React
      const url = `${API_BASE_URL}/doc_reader/process_digital_pdf?external_id=${encodeURIComponent(
        idToUse,
      )}&cell_id=${cell?.value}`;

      // Verificar o conteúdo do formData antes de enviar
      // Isso é apenas para diagnóstico e pode ser removido depois
      for (let pair of formData.entries()) {
        console.log(`FormData - ${pair[0]}: `, pair[1]);
      }

      // Fazer a primeira chamada para iniciar o processamento
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
          // Não definimos o Content-Type explicitamente pois o browser
          // o ajustará automaticamente com o boundary correto para FormData
        },
        body: formData,
      });

      if (!response.ok) {
        // Tentar obter mais detalhes do erro
        let errorDetail = null;
        try {
          const errorData = await response.json();
          // Se houver detail e for uma string não vazia, usar diretamente
          if (
            errorData?.detail &&
            typeof errorData.detail === "string" &&
            errorData.detail.trim()
          ) {
            errorDetail = errorData.detail;
          }
        } catch (parseError) {
          // Se não conseguir fazer parse, errorDetail permanece null
        }

        // Se houver detail, criar erro com ele; caso contrário, usar mensagem formatada
        if (errorDetail) {
          const error = new Error(errorDetail);
          error.detail = errorDetail;
          throw error;
        } else {
          throw new Error(
            t("common:requestError", {
              status: response.status,
              statusText: response.statusText,
            }),
          );
        }
      }

      const data = await response.json();

      if (!data.job_id) {
        throw new Error(t("common:invalidResponse"));
      }

      const jobId = data.job_id;
      console.log(`Job started successfully! Job ID: ${jobId}`);
      setProcessingStatus(t("processingDocument", { jobId }));
      setProcessingProgress(30);

      // Iniciar polling para verificar o status do job
      await checkJobStatus(jobId);
    } catch (error) {
      console.error("Error processing document:", error);
      // Usar error.detail se disponível, caso contrário usar error.message
      const errorMessage = error.detail || error.message;
      setError(t("processingFailed", { error: errorMessage }));
      setIsProcessing(false);
    }
  };

  const checkJobStatus = async (jobId) => {
    let isDone = false;
    let attempts = 0;
    let maxProgressValue = 90; // Progresso máximo antes de concluir

    // Resetar o estado de cancelamento
    setCancelProcessing(false);

    while (!isDone) {
      // Verificar se o usuário cancelou o processamento
      if (cancelProcessing) {
        console.log("Processamento cancelado pelo usuário.");
        setProcessingStatus(t("processingCancelled"));
        setIsProcessing(false);
        return;
      }

      try {
        // Esperar entre as verificações, aumentando o tempo de espera gradualmente
        if (attempts > 0) {
          const waitTime = Math.min(5000 + attempts * 1000, 15000); // Aumenta o tempo de espera, máximo de 15 segundos
          console.log(
            `Aguardando ${
              waitTime / 1000
            } segundos antes da próxima verificação...`,
          );
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }

        attempts++;
        // Calcula o progresso gradual, mas sem atingir 100% até concluir
        const progressValue =
          30 + Math.min(attempts * 2, maxProgressValue - 30);
        setProcessingProgress(progressValue);

        // Passar o job_id como parâmetro na URL conforme especificação da API
        const url = `${API_BASE_URL}/job/job_result?job_id=${encodeURIComponent(
          jobId,
        )}`;

        // Log para diagnóstico
        console.log(
          `Verificando status do job ${jobId} (tentativa ${attempts})...`,
        );
        console.log(`URL: ${url}`);

        const response = await fetch(url, {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          // Corpo vazio, pois o job_id agora está na URL
          body: "",
        });

        // Tratamento especial para códigos de erro específicos que indicam falha permanente
        if (response.status === 404) {
          throw new Error(
            `Job não encontrado (404). O job pode ter expirado ou não existir.`,
          );
        }

        if (response.status === 401 || response.status === 403) {
          throw new Error(
            `Erro de autenticação (${response.status}). Verifique sua API Key.`,
          );
        }

        if (!response.ok) {
          // Para outros códigos de erro, tentar obter detalhes mas continuar tentando
          console.error(
            `Erro na tentativa ${attempts}: ${response.status} ${response.statusText}`,
          );

          try {
            const errorData = await response.json();
            console.error(`Detalhes do erro:`, errorData);
          } catch (parseError) {
            console.error(`Não foi possível obter detalhes do erro.`);
          }

          // Mostrar erro temporário na interface, mas continuar tentando
          setProcessingStatus(
            `Erro temporário na verificação. Tentando novamente... (Tentativa ${attempts})`,
          );
          continue; // Continua o loop sem lançar exceção
        }

        const data = await response.json();
        setProcessingStatus(
          `Verificando status do processamento... (Tentativa ${attempts})`,
        );

        if (data.status === "DONE") {
          isDone = true;
          setProcessingProgress(100);
          setProcessingStatus(t("processingCompleted"));

          // Salvar os resultados separadamente
          setResult(data.result);
          setRawResult(data.raw_result || null);
          setResultExtractionMode(data.extraction_mode || null);
        } else if (data.status === "ERROR") {
          throw new Error(
            t("common:processingError", {
              error: data.error || t("common:unknownError"),
            }),
          );
        } else if (data.status === "PROCESSING") {
          // Continua no loop, ainda processando
          setProcessingStatus(
            `Document still processing... (Attempt ${attempts})`,
          );
        } else {
          // Status desconhecido, mas continua tentando
          console.warn(`Unknown status received: ${data.status}`);
          //setProcessingStatus(`Processing status: ${data.status} (Attempt ${attempts})`);
          setProcessingStatus(`Processing status: ${data.status}`);
        }
      } catch (error) {
        console.error("Erro ao verificar status do job:", error);

        // Apenas erros críticos que realmente impedem o funcionamento farão o processo parar
        if (
          error.message.includes("404") ||
          error.message.includes("401") ||
          error.message.includes("403") ||
          error.message.includes("Processing error")
        ) {
          setError(
            `Falha ao verificar status do processamento: ${error.message}`,
          );
          setIsProcessing(false);
          return;
        }

        // Para outros erros, mostra mensagem mas continua tentando
        setProcessingStatus(
          `Erro temporário: ${error.message}. Tentando novamente... (Tentativa ${attempts})`,
        );
        console.log("Continuando tentativas apesar do erro...");
      }
    }

    setIsProcessing(false);
  };

  // Função para cancelar o processamento
  const handleCancelProcessing = () => {
    setCancelProcessing(true);
    setProcessingStatus(t("cancellingProcessing"));
    // O cancelamento efetivo acontecerá no próximo ciclo do loop de checkJobStatus
  };

  return (
    <>
      <Paper className={classes.root}>
        <Typography variant="h6" gutterBottom>
          <Box fontWeight="fontWeightBold">{t("documentProcessing")}</Box>
        </Typography>
        <Divider className="mb-16" />

        {error && (
          <Alert
            severity="error"
            className="mb-16"
            style={{
              whiteSpace: "pre-line",
              textAlign: "center",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="External ID"
              variant="outlined"
              fullWidth
              className={classes.formField}
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              disabled={autoGenExternalId}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            style={{ display: "flex", alignItems: "center" }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoGenExternalId}
                  onChange={(e) => setAutoGenExternalId(e.target.checked)}
                  color="primary"
                />
              }
              label={t("autoGenerateExternalId")}
              style={{ marginLeft: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nome do Job"
              variant="outlined"
              fullWidth
              className={classes.formField}
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              required
              helperText="Nome identificador para este job. Será usado para busca e filtragem."
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl
              variant="outlined"
              fullWidth
              className={classes.formField}
              disabled={isProcessing}
            >
              <InputLabel id="extraction-mode-label">
                {t("extractionMode.label")}
              </InputLabel>
              <Select
                labelId="extraction-mode-label"
                id="extraction-mode"
                value={extractionMode}
                onChange={(e) => setExtractionMode(e.target.value)}
                label={t("extractionMode.label")}
              >
                {EXTRACTION_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {t(`extractionMode.options.${mode}`, {
                      defaultValue: mode,
                    })}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" className={classes.helperText}>
                {t(`extractionMode.descriptions.${extractionMode}`, {
                  defaultValue: "",
                })}
              </Typography>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="center"
              border={1}
              borderColor="var(--ikaro-border-default)"
              borderRadius={4}
              p={1}
            >
              <Description style={{ color: "var(--ikaro-text-secondary)" }} />
              <input
                ref={fileInputRef}
                className={classes.fileInput}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
              />
              <Typography className={classes.fileName}>
                {selectedFile ? selectedFile.name : t("noFileSelected")}
              </Typography>
              <Button
                variant="contained"
                color="default"
                className={classes.uploadButton}
                startIcon={<CloudUpload />}
                onClick={triggerFileInput}
                size="small"
              >
                Upload
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={processDocument}
            disabled={isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={20} /> : <ArrowRight />
            }
          >
            {isProcessing ? t("processing") : t("processDocument")}
          </Button>
        </Box>

        {isProcessing && (
          <Box className={classes.processingBox}>
            <CircularProgress
              variant="determinate"
              value={processingProgress}
              size={60}
            />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              {processingStatus}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className="mb-16"
            >
              {t("pleaseWait")}
            </Typography>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancelProcessing}
              size="small"
            >
              {t("cancelProcessing")}
            </Button>
          </Box>
        )}
      </Paper>

      {result && (
        <>
          {resultExtractionMode && (
            <Box
              mt={2}
              display="flex"
              alignItems="center"
              style={{ gap: 8 }}
            >
              <Typography variant="body2" color="textSecondary">
                {t("extractionMode.resultLabel")}
              </Typography>
              <ExtractionModeChip mode={resultExtractionMode} />
            </Box>
          )}
          <BoxRenderResult result={result} rawResult={rawResult} />
        </>
      )}
    </>
  );
};

export default DocumentProcessor;
